<script setup lang="ts">
import { computed, ref } from "vue";
import type { AdminExpertWalletBalancesSort } from "@shopexperts/legacy-api";
import { useAdminWalletStatistics } from "@/app/composables/wallet";
import WalletStatsOverviewCard from "./WalletStatsOverviewCard.vue";
import WalletActivityCard from "./WalletActivityCard.vue";
import WalletTopOwedCard from "./WalletTopOwedCard.vue";
import AdminExpertBalancesSection from "./AdminExpertBalancesSection.vue";

const props = defineProps<{
  /** Whether the statistics tab is the one on screen — gates all fetching. */
  active: boolean;
}>();

const emit = defineEmits<{
  viewTransactions: [organizationId: number];
}>();

const currency = ref<string | undefined>(undefined);
const enabled = computed(() => props.active);

const { statistics, isLoading, error } = useAdminWalletStatistics({
  currency,
  topLimit: 5,
  enabled,
});

const sort = ref<AdminExpertWalletBalancesSort>("total_owed");
const direction = ref<"asc" | "desc">("desc");
const includeSettled = ref(false);

const balancesSection = ref<InstanceType<typeof AdminExpertBalancesSection> | null>(null);

function scrollToBalances() {
  balancesSection.value?.$el?.scrollIntoView?.({ behavior: "smooth", block: "start" });
}

function onSortBy(key: "available" | "hold") {
  sort.value = key;
  direction.value = "desc";
  scrollToBalances();
}

function onShowNegative() {
  includeSettled.value = true;
  sort.value = "total_owed";
  direction.value = "asc";
  scrollToBalances();
}
</script>

<template>
  <div class="flex flex-col gap-6 pt-4">
    <WalletStatsOverviewCard
      :overview="statistics"
      :is-loading="isLoading"
      :error="error"
      :currency="currency"
      :active-sort="sort"
      @sort-by="onSortBy"
      @show-negative="onShowNegative"
      @change-currency="(next) => (currency = next)"
    />

    <div class="grid gap-4 lg:grid-cols-5">
      <WalletActivityCard
        class="lg:col-span-3"
        :activity="statistics?.activity"
        :currency="statistics?.currency"
        :is-loading="isLoading"
      />
      <WalletTopOwedCard
        class="lg:col-span-2"
        :top-experts="statistics?.top_experts"
        :total-owed-cents="statistics?.totals.total_owed_cents"
        :currency="statistics?.currency"
        :is-loading="isLoading"
        @view-transactions="(orgId) => emit('viewTransactions', orgId)"
      />
    </div>

    <AdminExpertBalancesSection
      ref="balancesSection"
      :active="active"
      :currency="currency"
      v-model:sort="sort"
      v-model:direction="direction"
      v-model:include-settled="includeSettled"
      @view-transactions="(orgId) => emit('viewTransactions', orgId)"
    />
  </div>
</template>
